// SceneryResolverCore — Set13 D10 inheritance for SCENERY KINDS, mirroring the material system's
// RecipeResolverCore. Walks `baseKindID` chains root-ward, applies `paramOverrides` outward
// (derived wins), and produces a structural hash (the bake/impostor cache key). Params-only value
// changes hash identically (the D10 "free variant" property — a params edit is a NEW variant, not
// a new program/bake).
//
// KEY DIFFERENCE from the material resolver: `paramOverrides` entries are PARTIAL PATCHES
// (`ParamSpread`), NOT whole-value replacements. Only the fields present apply — `{value: 18}`
// leaves the inherited swing/curve intact, `{swing: 0}` collapses an inherited variation without
// touching the value. A BARE value entry is treated as `{value: <bare>}` (the recipe-shaped
// shorthand). This is what lets ONE map carry value, swing and curve, and it is what makes
// `{swing: 0}` ("same tree, no variation") expressible. ⚠ Treating an override as a whole-object
// replacement silently resets the unmentioned fields — worth its own test (§7).
import {SceneryCollision, SceneryInstanceJitter, SceneryKindDef, type ParamOverride, type ParamSpread, type ResolvedSceneryParam} from "./@SceneryTypes.js";
import {resolveCollision, resolveNumber} from "./SceneryHash.js";

export const SCENERY_RESOLVER_DEPTH_CAP = 8;

export class SceneryResolutionError extends Error {
	constructor(message: string) { super(message); this.name = "SceneryResolutionError"; }
}

export interface ResolvedSceneryKind {
	id: string;
	name: string;
	generatorID: string;
	params: ResolvedSceneryParam[];
	/** Resolved placement defaults (extend chain + overrides apply to these too, via the same
	 *  partial-patch mechanism where applicable). */
	spacing: number;
	presence: number;
	groundAlign: SceneryKindDef["groundAlign"];
	/** Set14 P3 — NULLABLE: `null` = no grounding adjustment (generator's native min-Z), a number
	 *  = auto-ground then sink (§4.2 c). */
	sinkDepth: number|null;
	/** Composed at resolve time: `targetHeight × footprintRadiusRatio` (metres). `targetHeight`
	 *  comes from the resolved param of that key; kinds without one fall back to a default. */
	footprintRadius: number;
	/** Set17 — the RESOLVED collision shape (trunk/crown radii in metres) for the arbiter. Always
	 *  a concrete object: absent authored collision derives from spacing via the defaults
	 *  (trunk = 0.25×spacing, crown = 0.5×spacing — or `null` crown for ground-cover kinds, which
	 *  is decided by `SceneryCollision.crown == null` after the chain, since `null` crown is the
	 *  "no canopy" opt-out AND the "derive from spacing" sentinel for crown). */
	collision: SceneryCollision;
	jitter: SceneryKindDef["jitter"];
	variantCount: number;
	variantWeights: number[]|null;
	seed: number;
	/** Structural hash — the bake/impostor cache key. Invariant under params-only VALUE changes
	 *  (a variant is a params sample, so re-baking on every param edit would defeat the cache). */
	structuralHash: string;
	/** Human-readable canonical form underlying the hash (for debugging hash changes). */
	canonical: string;
	warnings: string[];
	chainIDs: string[];
}

/** Wrap a bare ParamValue into a `ParamSpread` patch (the `{value: x}` shorthand). Arrays and
 *  objects that are NOT a `ParamSpread` (ramp/color values) are wrapped as `{value: <it>}`. */
function toSpread(o: ParamOverride): ParamSpread {
	if (o == null) return {};
	if (typeof o != "object") return {value: o};
	// A ParamSpread has at least one of `value`/`swing`/`curve` as an own key.
	if ("value" in o || "swing" in o || "curve" in o) return o as ParamSpread;
	return {value: o as unknown as ParamSpread["value"]};
}

/** Patch one resolved param with a `ParamSpread` partial patch. ONLY present fields apply. */
function applySpread(param: ResolvedSceneryParam, patch: ParamSpread, warnings: string[]): void {
	if (patch.value != null) {
		// A `{value: 0}`/`{value: false}`/`{value: ""}` is a legit patch; distinguish "present"
		// via an own key check (the caller must have already wrapped a bare value into `{value:}`).
		param.value = patch.value;
	}
	if (patch.swing != null) {
		param.swing = patch.swing;
	}
	if (patch.curve != null) {
		param.curve = patch.curve == "log" ? "log" : "linear";
	}
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	warnings;
}

/** Resolve a kind's base chain (root → leaf) from a catalogue lookup. Throws
 *  {@link SceneryResolutionError} on unknown id, cycle, or depth-cap overflow. */
export function ResolveSceneryKind_ForTests(id: string, allKinds: SceneryKindDef[]): ResolvedSceneryKind {
	const chain: SceneryKindDef[] = [];
	const visited = new Set<string>();
	let cur = allKinds.find(k=>k.id == id) ?? null;
	if (cur == null) throw new SceneryResolutionError(`scenery kind "${id}" not found in catalogue`);
	while (cur != null) {
		if (visited.has(cur.id)) throw new SceneryResolutionError(`cycle detected: ${[...visited, cur.id].join(" -> ")}`);
		visited.add(cur.id);
		if (visited.size > SCENERY_RESOLVER_DEPTH_CAP) throw new SceneryResolutionError(`depth cap (${SCENERY_RESOLVER_DEPTH_CAP}) exceeded: ${[...visited].join(" -> ")}`);
		chain.unshift(cur);
		if (cur.baseKindID == null) break;
		cur = allKinds.find(k=>k.id == cur!.baseKindID) ?? null;
	}
	return resolveSceneryKindFromChain(chain);
}

/** Resolve a resolved-kind chain (root → leaf) into a flat `ResolvedSceneryKind`. */
export function resolveSceneryKindFromChain(chain: SceneryKindDef[]): ResolvedSceneryKind {
	const leaf = chain[chain.length - 1];
	const id = leaf.id;
	const warnings: string[] = [];

	// Collect params root-first, derived later wins (a descendant re-declaring a param key
	// replaces it wholesale — the params array is the union of the chain's declared params).
	const params: ResolvedSceneryParam[] = [];
	for (const kind of chain) {
		for (const schema of kind.params) {
			// Later declarations override earlier ones with the same key.
			const existing = params.findIndex(p=>p.key == schema.key);
			const resolved: ResolvedSceneryParam = {
				key: schema.key,
				schema,
				value: schema.default,
				fallback: schema.default,
				swing: typeof schema.swingDefault == "number" ? schema.swingDefault : 0,
				curve: schema.curveDefault == "log" ? "log" : "linear",
			};
			if (existing >= 0) params.splice(existing, 1, resolved);
			else params.push(resolved);
		}
	}

	// paramOverrides — derived wins (apply in chain order, later recipes override earlier).
	// `fallback` freezes just BEFORE the leaf's own override: only non-leaf overrides advance it,
	// so a leaf override leaves fallback = the value the param resolves to without the current kind's patch.
	for (const kind of chain) {
		for (const [key, override] of Object.entries(kind.paramOverrides)) {
			const p = params.find(p=>p.key == key);
			if (p == null) {
				warnings.push(`scenery kind "${kind.id}" overrides param "${key}" which is not in the resolved params`);
				continue;
			}
			applySpread(p, toSpread(override), warnings);
			if (kind !== leaf) p.fallback = p.value;
		}
	}

	// Placement defaults — walk the chain leaf-ward, taking the LAST explicitly-set value where
	// the field is numeric (0/false ARE explicit). We follow the "derive wins, and a derived def
	// leaves a field alone unless it explicitly overrides it" rule: a field absent from a
	// descendant inherits the base's resolved value. `undefined` (via `data` spread) means "not
	// overridden" — so we take the last non-undefined.
	const chainValue = <T,>(get: (k: SceneryKindDef)=>T|n|undefined): T|n|undefined=>{
		for (let i = chain.length - 1; i >= 0; i--) {
			const v = get(chain[i]);
			if (v != null) return v;
		}
		return undefined;
	};
	const spacing = chainValue(k=>k.spacing) ?? 6;
	const presence = chainValue(k=>k.presence) ?? 0.8;
	const groundAlign = chainValue(k=>k.groundAlign) ?? "up";
	const sinkDepth = chainValue(k=>k.sinkDepth) ?? null;
	const footprintRadiusRatio = chainValue(k=>k.footprintRadiusRatio) ?? 0.5;
	// Compose the resolved footprint radius from the resolved `targetHeight` param (the kind's
	// height is its scale knob — the kind IS targetHeight tall), so resizing via targetHeight
	// keeps the width proportional. Kinds with no `targetHeight` param (test harnesses / future
	// non-tree generators) fall back to this default height (metres).
	const targetHeightParam = params.find(p=>p.key == "targetHeight");
	const resolvedTargetHeight = (targetHeightParam && typeof targetHeightParam.value == "number" && Number.isFinite(targetHeightParam.value) && targetHeightParam.value > 0)
		? targetHeightParam.value
		: 12;
	const footprintRadius = resolvedTargetHeight * footprintRadiusRatio;
	const jitter = chainValue(k=>k.jitter) ?? new SceneryInstanceJitter();
	const variantCount = chainValue(k=>k.variantCount) ?? 6;
	const variantWeights = chainValue(k=>k.variantWeights) ?? null;
	const seed = chainValue(k=>k.seed) ?? 0;
	// Set17 — resolve the collision shape through the shared seam (so the resolver and the placer
	// can never disagree about how absent/null authored fields fall back). Absent collision
	// derives from the resolved spacing (trunk = 0.25×spacing, crown = 0.5×spacing); an explicit
	// `null` crown is preserved as the ground-cover opt-out.
	const collision = resolveCollision(spacing, chainValue(k=>k.collision));
	// generatorID — INHERIT through the chain like the placement fields (the leaf's `baseKindID`
	// "extend" def doesn't redeclare it; taking the leaf alone yields "" → the bake's "unknown
	// generator" degrade path → an all-black variant preview). A leaf that explicitly overrides it
	// (e.g. a fork with a different generator) wins. NOTE: unlike the placement fields (whose
	// "not overridden" sentinel is `undefined`), a def's class-default generatorID is `""` — so
	// `""` must be treated as "not set" here too, or the leaf's empty string shadows the base's ID.
	const generatorID = chainValue(k=>k.generatorID || undefined) ?? "";

	// Structural hash — excludes param VALUES (params contribute keys+kinds+swing+curve only).
	// Placement numeric fields participate (they change what's placed but not the mesh/atlas —
	// keep them OUT of the mesh hash; the ATLAS (bake) key does include params+seed+light rig,
	// so placement fields don't belong there either. They DO belong in a placement-layer key —
	// but that's derived per-region, not per-bake. Keep the structural hash mesh-focused: the
	// params' swing/curve ARE structural (they change the variant SAMPLE SPACE → the derived
	// variants → the mesh), so they hash in. The `variantCount`/`variantWeights`/`seed` also
	// change the derived variants, so they hash in too.)
	const {canonical, hash} = structuralSceneryHash(chain, params, variantCount, variantWeights, seed);
	return {
		id, name: leaf.name, generatorID,
		params,
		spacing, presence, groundAlign, sinkDepth, footprintRadius,
		collision,
		jitter, variantCount, variantWeights, seed,
		structuralHash: hash, canonical, warnings,
		chainIDs: chain.map(k=>k.id),
	};
}

/** Structural hash (bake/atlas cache key) + canonical form.  Kind ID DELIBERATELY excluded —
 *  identical resolved structures → identical derived variants → one shared atlas bake (the D10
 *  free-variant property).  Params contribute keys+kinds+swing+curve ONLY (never values — a value
 *  edit is a FREE variant, one bake reused across params samples).  Literal placement fields hash
 *  by value. */
function structuralSceneryHash(
	chain: SceneryKindDef[],
	params: ResolvedSceneryParam[],
	variantCount: number,
	variantWeights: number[]|null,
	seed: number,
): {canonical: string; hash: string} {
	const parts: string[] = [];
	parts.push(`N:${variantCount}`);
	parts.push(`W:${variantWeights == null ? "-" : variantWeights.join(",")}`);
	parts.push(`S:${seed}`);
	for (const p of params) {
		parts.push(`P:${p.key}:${p.schema.kind}:${p.swing}:${p.curve}`);
	}
	const canonical = parts.join("|");
	return {canonical, hash: fnv1a(canonical)};
}

/** FNV-1a 32-bit, hex-encoded — compact deterministic hash of the canonical form. */
function fnv1a(s: string): string {
	let h = 0x811c9dc5;
	for (let i = 0; i < s.length; i++) {
		h ^= s.charCodeAt(i);
		h = (h * 0x01000193) >>> 0;
	}
	return h.toString(16).padStart(8, "0");
}
